//
//  Projects.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 22, 2026
//  Author: Bob Keathley
//
//  Projects list (§6s): one row per bound folder the tenant knows about, with
//  an Import snapshot file action for team-admin and above.
//
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { api, getAssumed, subscribeAssumed } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { canEditProjects, projectErrorText, type ImportResult, type ProjectPage, type ProjectSummary } from '@shared/api/projects'
import type { Session } from '@shared/auth'
import { href, navigate } from '@shared/router'

export const when = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleString() : '—')

/** Import is team-admin+; disabled with a reason in an assumed read session. */
export function importState(role: string | undefined, assumed: { mode: 'read' | 'write' } | null): { visible: boolean; reason: string | null } {
  if (!canEditProjects(role)) return { visible: false, reason: null }
  if (assumed?.mode === 'read') return { visible: true, reason: 'This is an assumed read-only session; it cannot import.' }
  return { visible: true, reason: null }
}

export default function Projects({ session }: { session: Session }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const imp = importState(session.user.role, assumed)
  const [items, setItems] = useState<ProjectSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    const r = await api<ProjectPage<ProjectSummary>>('/projects', {}, session.token)
    if (isOk(r)) { setItems(r.data.items); setError(null) } else { setItems([]); setError(r.error.message) }
  }, [session.token])
  useEffect(() => { load() }, [load])

  async function onFile(file: File | undefined) {
    if (!file) return
    setError(null); setNotice(null); setBusy(true)
    const text = await file.text()
    try { JSON.parse(text) } catch { setBusy(false); setError('That file is not a JSON snapshot.'); return }
    const r = await api<ImportResult>('/projects/import', { method: 'POST', body: text }, session.token)
    setBusy(false)
    if (fileRef.current) fileRef.current.value = ''
    if (!isOk(r)) { setError(projectErrorText(r.error)); return }
    setNotice(`${r.data.created ? 'Created a project' : 'Updated the project'} from the snapshot. ${r.data.agents_changed.length} agent${r.data.agents_changed.length === 1 ? '' : 's'} new or changed.`)
    load()
    navigate(`/projects/${r.data.project_id}`)
  }

  return (
    <div className="projects">
      <h2>Projects</h2>
      <p className="hint">Each project is a bound folder: its repos, agents, rules and skills. Agents here are what the next project release ships.</p>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}
      {imp.visible && (
        <p>
          <input ref={fileRef} type="file" accept="application/json,.json" aria-label="Snapshot file" style={{ display: 'none' }} onChange={(e) => onFile(e.target.files?.[0])} />
          <button className="btn" disabled={busy || !!imp.reason} title={imp.reason ?? undefined} onClick={() => fileRef.current?.click()}>{busy ? 'Importing…' : 'Import snapshot file'}</button>
          {imp.reason && <span className="hint"> {imp.reason}</span>}
        </p>
      )}
      <table>
        <thead><tr><th>Project</th><th>Repos</th><th>Agents</th><th>Last scan</th><th>Drift</th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={5} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && <tr><td colSpan={5} className="hint">No projects yet. Bind a folder and scan it in the Signet app{imp.visible ? ', or import a snapshot file' : ''}.</td></tr>}
          {items?.map((p) => (
            <tr key={p.id}>
              <td><a href={href(`/projects/${p.id}`)}>{p.name}</a>{p.description && <div className="hint">{p.description}</div>}</td>
              <td>{p.repo_count}</td>
              <td>{p.agent_count}</td>
              <td className="hint">{when(p.last_snapshot_at)}</td>
              <td>{p.drift_count > 0 ? <span className="pill drift">{p.drift_count} drifted</span> : <span className="hint">none</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
